import { useEffect, useRef } from 'react';

/**
 * Renders one or more JSON-LD blocks into <head> as
 * <script type="application/ld+json"> tags.
 *
 * react-helmet-async historically does not render `<script>` reliably, so we
 * inject directly via DOM and clean up on unmount / data change. This is the
 * approach Next.js's `<Script type="application/ld+json">` ultimately ends
 * up doing as well.
 */
const StructuredData = ({ data }) => {
  const injectedRef = useRef([]);

  useEffect(() => {
    // Clean previous round of scripts so navigation between products doesn't
    // accumulate stale JSON-LD.
    for (const el of injectedRef.current) {
      if (el && el.parentNode) el.parentNode.removeChild(el);
    }
    injectedRef.current = [];
    if (!data) return;
    const items = Array.isArray(data) ? data : [data];
    for (const item of items) {
      if (!item) continue;
      const tag = document.createElement('script');
      tag.type = 'application/ld+json';
      tag.dataset.sunifySchema = '1';
      tag.text = JSON.stringify(item);
      document.head.appendChild(tag);
      injectedRef.current.push(tag);
    }
    return () => {
      for (const el of injectedRef.current) {
        if (el && el.parentNode) el.parentNode.removeChild(el);
      }
      injectedRef.current = [];
    };
  }, [data]);

  return null;
};

/** Build a schema.org Product object from our internal product shape. */
export const buildProductSchema = (product, { language = 'sk', siteUrl } = {}) => {
  if (!product) return null;
  const translations = product.translations || {};
  const tr = translations[language] || translations.sk || {};
  const name = tr.name || product.sku || '';
  const description = tr.description || name;
  const image = product.images && product.images.length > 0 ? product.images : undefined;
  const stock = Number(product.stock || 0);
  const available = stock > 0;
  const price = Number(product.price_eur || 0);
  const brand = product.brand || 'Sunify';
  const productUrl = `${siteUrl || ''}/product/${product.id}`;

  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name,
    description,
    sku: product.sku || product.id,
    brand: { '@type': 'Brand', name: brand },
    url: productUrl,
    offers: {
      '@type': 'Offer',
      url: productUrl,
      priceCurrency: 'EUR',
      price: price.toFixed(2),
      availability: available
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      itemCondition: 'https://schema.org/NewCondition',
    },
  };
  if (image) schema.image = image;
  // Only emit ratings when we have a trustworthy sample
  const ratingCount = Number(product.rating_count || 0);
  const ratingAvg = Number(product.rating_avg || 0);
  if (ratingCount >= 3 && ratingAvg > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: ratingAvg.toFixed(1),
      reviewCount: ratingCount,
      bestRating: '5',
      worstRating: '1',
    };
  }
  return schema;
};

/** Build a schema.org BreadcrumbList from an ordered array of
 * `{ name, url }` items. `url` may be a relative path; we prepend siteUrl. */
export const buildBreadcrumbSchema = (items, { siteUrl = '' } = {}) => {
  if (!items || !items.length) return null;
  return {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: it.url.startsWith('http') ? it.url : `${siteUrl}${it.url}`,
    })),
  };
};

export default StructuredData;
